// The client's pay page (/i/<token>): the invoice, the ways to pay, the card box, tips, the receipt.
import { byYard, cardKeptSentence, clientLinkTitle, yardLabel } from "@steward/shared";
import { loadStripe, type Stripe, type StripeElements } from "@stripe/stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { finishPay, paySaved, type PayStart, startPay, startTip, testPay, usePublicInvoice } from "../../api/money";
import { CardTermsLink } from "../../components/CardFields";
import { moneyExact } from "../../components/format";
import { Banner, prettyPhone, Skeleton } from "../../components/parts";
import { errText } from "../../components/work";
import { Branded, brandWord, CardLogo, NotFound } from "./common";
import { SeeIncluded } from "./QuotePage";

/** Stripe's names for the ways a page can take (the server makes the payment with the same ones). */
const STRIPE_WAY = { card: "card", bank: "us_bank_account", klarna: "klarna" } as const;

/** The pay page's way buttons, the old Steward's words and icons (invpay.php). */
const WAY_WORDS = { card: "Credit / Debit", bank: "Bank payment", klarna: "Klarna" } as const;

const WAY_ICON = {
  card: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="M2.5 10h19" />
    </svg>
  ),
  bank: (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 9l9-5 9 5" />
      <path d="M5 9v9M9.5 9v9M14.5 9v9M19 9v9M3 20h18" />
    </svg>
  ),
  klarna: (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="4" fill="#ffb3c7" />
      <path d="M8 8v8M14 8c0 2.5-1.5 4-3.5 4L15 16" fill="none" stroke="#0b051d" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
} as const;

const LOCK = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

/** "Oct 30, 2026" from a day or a time. */
const longDay = (iso: string) =>
  new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T12:00:00` : iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

/**
 * The pay page, laid out like the old Steward's (invpay.php; the owner's shots, 2026-10-30): their name and the brand on top,
 * the invoice folded under View invoice (as Jobber's client hub), the amount large, the optional tip, then Payment method
 * with Stripe's card fields open from the start, Confirm and Pay in the brand's button color, the one-time charge words,
 * the cards taken, and the three trust points.
 */
export function PayPage({ token, session }: { token: string; session?: string }) {
  // session: the payment_intent Stripe adds to the address when it comes back here after a bank check (3D Secure).
  const inv = usePublicInvoice(token);
  const qc = useQueryClient();
  const [stripe, setStripe] = useState<{ s: Stripe; e: StripeElements } | null>(null);
  const cardBox = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<number | "other" | null>(null);
  const [other, setOther] = useState("");
  const [name, setName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const finished = useRef(false);
  // A paid invoice shows the check and thanks; the tip box and card fields open only when they press Add a tip.
  const [tipOpen, setTipOpen] = useState(false);
  // The payment went through: the form folds away before the paid check comes in.
  const [closing, setClosing] = useState(false);
  const i = inv.data;

  // Back from Stripe: tell the server once. The webhook may have marked it paid already; paying stays one time either way.
  useEffect(() => {
    if (!session || finished.current) return;
    finished.current = true;
    void finishPay(token, session)
      .then(() => qc.invalidateQueries({ queryKey: ["pay", token] }))
      .catch((e: unknown) => setErr(errText(e, "We could not check the payment. Refresh in a minute.")));
  }, [session, token, qc]);

  const canTip = !!i && i.status === "paid" && i.tipUntil !== null && i.tipChoices.length > 0;
  const tipAfter = canTip && tipOpen;
  const open = !!i && ((i.status === "open" && i.balanceCents > 0 && i.bankOnTheWayCents === 0) || tipAfter);
  const otherCents = Math.round(Number(other.replace(/[$,\s]/g, "")) * 100);
  const tipCents =
    !i || tip === null
      ? 0
      : tip === "other"
        ? Math.max(0, otherCents || 0)
        : (i.tipChoices.find((c) => c.percent === tip)?.cents ?? 0);
  const payCents = (tipAfter ? 0 : (i?.balanceCents ?? 0)) + tipCents;
  const pk = i?.publishableKey ?? null;
  const ways: readonly ("card" | "bank" | "klarna")[] = tipAfter ? ["card"] : (i?.payWays ?? ["card"]);
  // The way picked with the buttons, like the old Steward's pay page: Credit / Debit, Bank payment, Klarna.
  const [picked, setPicked] = useState<"card" | "bank" | "klarna">("card");
  const kind = ways.includes(picked) ? picked : "card";
  // The card they saved before, picked from the start (owner, 2026-11-01); Use a different card opens Stripe's fields.
  const [newCard, setNewCard] = useState(false);
  const savedCard = i?.cardOnFile && i.canPayCard && !tipAfter && kind === "card" && !newCard ? i.cardOnFile : null;
  // A card payment always keeps the card on file (owner, 2026-11-01); a tip after paying or the saved card does not.
  const save = kind === "card" && !tipAfter && !savedCard;
  const fieldsOn = savedCard === null;
  // Charges after each visit (owner, 2026-11-02): said on the page where paying is the OK (offer), a box to tick where the
  // brand asks first (ask). Only for a card payment, never a tip after paying.
  const [autoTick, setAutoTick] = useState(false);
  const autoBox = kind === "card" && !tipAfter && i?.autoCharge === "ask";
  const autoAfter = kind === "card" && !tipAfter && (i?.autoCharge === "offer" || (autoBox && autoTick));
  const autoOk = autoBox && autoTick;
  const walletBox = useRef<HTMLDivElement>(null);
  const [wallet, setWallet] = useState<{ s: Stripe; e: StripeElements } | null>(null);
  const [hasWallet, setHasWallet] = useState(false);
  // What the Apple Pay or Google Pay button pays with, read when it is pressed (it is made once).
  const live = useRef({ tipCents: 0, save: false, tipAfter: false, autoOk: false });
  const paidNow = async () => {
    setClosing(true);
    await new Promise((r) => setTimeout(r, matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 450));
    await qc.invalidateQueries({ queryKey: ["pay", token] });
    setTip(null);
    setTipOpen(false);
    setClosing(false);
  };
  live.current = { tipCents, save, tipAfter, autoOk };

  // Stripe's fields for the way picked, open from the start (the payment is made when they press Confirm and Pay).
  useEffect(() => {
    if (!pk || !open || !fieldsOn || !(tipAfter || i.canPayCard)) return;
    let gone = false;
    let drop: (() => void) | null = null;
    void loadStripe(pk).then((s) => {
      if (!s || gone || !cardBox.current) return;
      const e = s.elements({
        mode: "payment",
        amount: Math.max(50, payCents),
        currency: "usd",
        paymentMethodTypes: [STRIPE_WAY[kind]],
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: i.brand.look?.accent ?? "#1f6f5c",
            borderRadius: "8px",
            fontFamily: "Inter, system-ui, sans-serif",
          },
        },
        fonts: [{ cssSrc: "https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap" }],
      });
      // Just the fields, like the old page: Apple Pay and Google Pay have their own button above, and the country is US.
      const el = e.create("payment", {
        layout: "tabs",
        fields: { billingDetails: { name: kind === "card" ? "never" : "auto", address: { country: "never" } } },
        wallets: { applePay: "never", googlePay: "never", link: "never" },
        terms: { card: "never" },
      });
      el.mount(cardBox.current);
      drop = () => el.destroy();
      setStripe({ s, e });
    });
    return () => {
      gone = true;
      drop?.();
      setStripe(null);
    };
    // Made once per way; the amount and saving the card are passed on with update below.
  }, [pk, open, kind, fieldsOn]);

  // Apple Pay and Google Pay above the card fields, shown only on a phone or browser that has one.
  useEffect(() => {
    if (!pk || !open || !fieldsOn || kind !== "card" || !(tipAfter || i.canPayCard)) return;
    let gone = false;
    let drop: (() => void) | null = null;
    void loadStripe(pk).then((s) => {
      if (!s || gone || !walletBox.current) return;
      const e = s.elements({ mode: "payment", amount: Math.max(50, payCents), currency: "usd", paymentMethodTypes: ["card"] });
      const ece = e.create("expressCheckout", {
        buttonHeight: 52,
        paymentMethods: { applePay: "auto", googlePay: "auto", link: "never", paypal: "never", amazonPay: "never" },
      });
      ece.on("ready", (ev) => {
        if (!gone) setHasWallet(Boolean(ev.availablePaymentMethods));
      });
      ece.on("confirm", () => {
        void (async () => {
          const now = live.current;
          setBusy(true);
          setErr(null);
          try {
            const checked = await e.submit();
            if (checked.error) throw new Error(checked.error.message ?? "That did not go through.");
            const made = now.tipAfter
              ? await startTip(token, now.tipCents)
              : await startPay(token, now.tipCents, now.save, "card", now.autoOk);
            const r = await s.confirmPayment({
              elements: e,
              clientSecret: made.clientSecret,
              confirmParams: { return_url: window.location.href },
              redirect: "if_required",
            });
            if (r.error) throw new Error(r.error.message ?? "The payment did not go through.");
            await finishPay(token, made.intentId);
            await paidNow();
          } catch (x) {
            setErr(
              x instanceof Error && !(x instanceof TypeError) ? x.message : errText(x, "That did not go through. Try again."),
            );
          } finally {
            setBusy(false);
          }
        })();
      });
      ece.mount(walletBox.current);
      drop = () => ece.destroy();
      setWallet({ s, e });
    });
    return () => {
      gone = true;
      drop?.();
      setWallet(null);
      setHasWallet(false);
    };
  }, [pk, open, kind, fieldsOn]);
  useEffect(() => {
    const keep = save ? "off_session" : null;
    stripe?.e.update({ amount: Math.max(50, payCents), setupFutureUsage: keep });
    wallet?.e.update({ amount: Math.max(50, payCents), setupFutureUsage: keep });
  }, [stripe, wallet, payCents, save, tipAfter, kind]);

  if (inv.error) return <NotFound what="invoice" />;
  if (!i)
    return (
      <Branded brand={null}>
        <Skeleton rows={6} />
      </Branded>
    );

  const late = i.dueAt !== null && new Date(i.dueAt).getTime() < Date.now();
  const chip =
    i.status === "paid"
      ? { words: "Paid", cls: "ok" }
      : i.bankOnTheWayCents > 0
        ? { words: "Processing", cls: "ok" }
        : i.status === "void"
          ? { words: "Closed", cls: "" }
          : late
            ? { words: "Past due", cls: "late" }
            : { words: "Awaiting payment", cls: "" };
  const cardName = name ?? i.client?.name ?? "";
  const canPay = tipAfter || i.canPayCard;

  const confirm = async () => {
    if (tip === "other" && otherCents > i.maxTipCents) {
      setErr(`A tip can be up to ${moneyExact(i.maxTipCents)}.`);
      return;
    }
    if (tipAfter && tipCents <= 0) {
      setErr("Pick a tip first.");
      return;
    }
    setBusy(true);
    setErr(null);
    if (savedCard) {
      try {
        const r = await paySaved(token, tipCents, i.balanceCents, savedCard.last4, autoOk);
        if (r.paid) await paidNow();
        else setErr(`${r.failure ?? "The card did not go through."} Try a different card.`);
      } catch (e) {
        setErr(e instanceof Error && !(e instanceof TypeError) ? e.message : errText(e, "That did not go through. Try again."));
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      if (stripe) {
        const checked = await stripe.e.submit();
        if (checked.error) throw new Error(checked.error.message ?? "Check the card details.");
      }
      const made: PayStart = tipAfter ? await startTip(token, tipCents) : await startPay(token, tipCents, save, kind, autoOk);
      if (made.fake || !stripe) {
        await testPay(token, made.intentId);
      } else {
        const r = await stripe.s.confirmPayment({
          elements: stripe.e,
          clientSecret: made.clientSecret,
          confirmParams: {
            return_url: window.location.href,
            // The bank side asks for the name itself; the card side takes it from Name on card.
            payment_method_data: {
              billing_details:
                kind === "card"
                  ? { name: cardName.trim() || (i.client?.name ?? ""), address: { country: "US" } }
                  : { address: { country: "US" } },
            },
          },
          redirect: "if_required",
        });
        if (r.error) throw new Error(r.error.message ?? "The card did not go through.");
        await finishPay(token, made.intentId);
      }
      await paidNow();
    } catch (e) {
      setErr(e instanceof Error && !(e instanceof TypeError) ? e.message : errText(e, "That did not go through. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const website = i.brand.website ? i.brand.website.replace(/\/$/, "") : "";
  return (
    <Branded
      brand={i.brand}
      account={`/my?i=${token}`}
      title={clientLinkTitle("invoice", { brand: i.brand.name, number: i.number }).title}
    >
      <div className="cp">
        {/* Their name on top, so the right client knows it is theirs and anyone sent it by mistake sees it is not. */}
        <p className="cp-hi">
          Hi {i.firstName}, thank you for choosing {i.brand.name}.
        </p>

        <details className="cp-inv">
          <summary>
            <span className="cp-sv">View invoice</span>
            <span className="cp-sh">Hide invoice</span>
          </summary>
          <div className="cp-body">
            <div className="cp-ihead">
              <span className="cp-inum">{i.number ? `Invoice #${String(i.number)}` : "Invoice"}</span>
              <span className={`cp-chip ${chip.cls}`}>{chip.words}</span>
            </div>
            {i.jobName ? <div className="cp-job">{i.jobName}</div> : null}
            {i.home ? <div className="cp-fact">For your home, {i.home}</div> : null}
            {i.client ? <div className="cp-who">{i.client.name}</div> : null}
            {i.client?.address ? <div className="cp-fact">{i.client.address}</div> : null}
            {i.client?.phone ? <div className="cp-fact">{prettyPhone(i.client.phone)}</div> : null}
            <div className="cp-rows">
              {i.poNumber ? (
                <div className="cp-row">
                  <span>Work order / PO #</span>
                  <span>{i.poNumber}</span>
                </div>
              ) : null}
              {i.issuedOn ? (
                <div className="cp-row">
                  <span>Issued</span>
                  <span>{longDay(i.issuedOn)}</span>
                </div>
              ) : null}
              <div className="cp-row">
                <span>Due</span>
                <span>{i.dueAt ? longDay(i.dueAt) : "Upon receipt"}</span>
              </div>
            </div>
            <div className="cp-items">
              {i.lines.map((l, n) => (
                <div className="cp-item" key={n}>
                  <div>
                    <div className="cp-iname">
                      {l.description}
                      {l.quantity !== 1 ? ` x ${String(l.quantity)}` : ""}
                    </div>
                    {l.serviceDate || l.note ? (
                      <div className="cp-idesc">
                        {[l.serviceDate ? longDay(l.serviceDate) : null, l.note].filter(Boolean).join(" · ")}
                      </div>
                    ) : null}
                    <SeeIncluded link={l.link} />
                  </div>
                  <div className="cp-iamt">{moneyExact(l.amountCents)}</div>
                </div>
              ))}
            </div>
            <div className="cp-sum">
              <span>Subtotal</span>
              <span>{moneyExact(i.subtotalCents)}</span>
            </div>
            {i.tipCents ? (
              <div className="cp-sum">
                <span>Tip</span>
                <span>{moneyExact(i.tipCents)}</span>
              </div>
            ) : null}
            <div className="cp-sum big">
              <span>Total</span>
              <span>{moneyExact(i.totalCents)}</span>
            </div>
            {i.paidCents > 0 && i.balanceCents > 0 ? (
              <div className="cp-sum">
                <span>Paid so far</span>
                <span>{moneyExact(-i.paidCents)}</span>
              </div>
            ) : null}
            <div className="cp-sum">
              <span>{i.paidCents > 0 && i.balanceCents > 0 ? "Balance due" : "Invoice balance"}</span>
              <span>{moneyExact(i.status === "paid" ? 0 : Math.max(0, i.balanceCents))}</span>
            </div>
            {i.noteToClient ? <p className="cp-msg">{i.noteToClient}</p> : null}
            <p className="cp-thanks">Thank you for choosing {i.brand.name}.</p>
            <a className="cp-pdf" href={`/v1/public/pay/${token}/pdf`} target="_blank" rel="noopener">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
              </svg>
              Download PDF
            </a>
          </div>
        </details>

        {i.status === "paid" ? (
          <div className="cp-paid" role="status">
            <svg className="cp-check" viewBox="0 0 52 52" aria-hidden="true">
              <circle cx="26" cy="26" r="25" />
              <path d="M15 27.5l7 7 15-15" />
            </svg>
            <div className="cp-paidh">Paid in full</div>
            <div className="cp-paidamt">{moneyExact(i.totalCents)}</div>
            <p className="cp-paidty">Thank you for supporting us.</p>
            {i.tipCents > 0 ? <div className="cp-tipdone">Tip for your team {moneyExact(i.tipCents)}. Thank you.</div> : null}
            {canTip ? (
              <button type="button" className="cp-addtip" aria-expanded={tipOpen} onClick={() => setTipOpen(!tipOpen)}>
                Want to add a tip?
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
            ) : null}
          </div>
        ) : i.status === "void" ? (
          <Banner type="notice">This invoice was closed. You owe nothing on it.</Banner>
        ) : (
          <>
            <div className="cp-lbl">Payment amount</div>
            <div className="cp-due">{moneyExact(i.balanceCents + tipCents)}</div>
            {tipCents > 0 ? (
              <div className="cp-tipdone">Includes a {moneyExact(tipCents)} tip for your team. Thank you.</div>
            ) : null}
          </>
        )}

        {i.bankOnTheWayCents > 0 ? (
          <Banner type="notice">
            Your bank payment of {moneyExact(i.bankOnTheWayCents)} is on its way. Banks take 3 to 5 business days to clear it.
          </Banner>
        ) : null}

        <div className={`cp-fold${closing ? " shut" : ""}`}>
          <div className={tipAfter ? "cp-drop" : undefined}>
            {open && i.tipChoices.length ? (
              <>
                <div className="cp-tipbox">
                  <div className="cp-tiph">Say thanks with a tip</div>
                  <div className="cp-tips" role="group" aria-label="Tip">
                    {i.tipChoices.map((c) => (
                      <button
                        key={c.percent}
                        type="button"
                        className="cp-tip"
                        aria-pressed={tip === c.percent}
                        title={moneyExact(c.cents)}
                        onClick={() => setTip(tip === c.percent ? null : c.percent)}
                      >
                        {c.percent}%
                      </button>
                    ))}
                    <button
                      type="button"
                      className="cp-tip"
                      aria-pressed={tip === "other"}
                      onClick={() => setTip(tip === "other" ? null : "other")}
                    >
                      Other amount
                    </button>
                  </div>
                  {tip === "other" ? (
                    <div className="cp-tipother">
                      <input
                        inputMode="decimal"
                        aria-label="Tip in dollars"
                        placeholder="Tip in dollars"
                        value={other}
                        onChange={(e) => setOther(e.target.value)}
                      />
                    </div>
                  ) : null}
                </div>
                <p className="cp-tipnote">Tip is based on your invoice subtotal</p>
              </>
            ) : null}

            {open ? (
              canPay ? (
                <>
                  <h2 className="cp-h">{tipAfter ? "Pay your tip" : "Payment method"}</h2>
                  {ways.length > 1 ? (
                    <div className="cp-ways" role="radiogroup" aria-label="Payment method">
                      {ways.map((w) => (
                        <button
                          key={w}
                          type="button"
                          role="radio"
                          aria-checked={kind === w}
                          className={`cp-way${kind === w ? " on" : ""}`}
                          onClick={() => {
                            setPicked(w);
                            setErr(null);
                          }}
                        >
                          {WAY_ICON[w]}
                          <span>{WAY_WORDS[w]}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <div className="cp-pmbox">
                    {i.cardOnFile && kind === "card" && !tipAfter ? (
                      <div className="cp-cards" role="radiogroup" aria-label="Card">
                        <button
                          type="button"
                          role="radio"
                          aria-checked={!newCard}
                          className="cp-cardpick"
                          onClick={() => {
                            setNewCard(false);
                            setErr(null);
                          }}
                        >
                          <span className="cp-dot" aria-hidden="true" />
                          <span>
                            {brandWord(i.cardOnFile.brand)} ending {i.cardOnFile.last4}
                            <small>Saved card</small>
                          </span>
                          <CardLogo brand={i.cardOnFile.brand} />
                        </button>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={newCard}
                          className="cp-cardpick"
                          onClick={() => {
                            setNewCard(true);
                            setErr(null);
                          }}
                        >
                          <span className="cp-dot" aria-hidden="true" />
                          <span>Use a different card</span>
                        </button>
                      </div>
                    ) : null}
                    {kind === "card" && fieldsOn ? (
                      <>
                        <div ref={walletBox} className={`cp-wallet${hasWallet ? " on" : ""}`} />
                        {hasWallet ? <div className="cp-or">OR</div> : null}
                        <label className="cp-fld">
                          Name on card
                          <input type="text" autoComplete="cc-name" value={cardName} onChange={(e) => setName(e.target.value)} />
                        </label>
                      </>
                    ) : null}
                    {!fieldsOn ? null : pk ? (
                      <div ref={cardBox} className="cp-card" />
                    ) : (
                      <Banner type="notice">Card pay is in test mode here. Pay uses the test card 4242.</Banner>
                    )}
                    {kind === "bank" ? (
                      <p className="cp-panehelp">
                        Log in to your bank to pay from your checking or savings account. A bank payment takes 3 to 5 business
                        days to clear.
                      </p>
                    ) : null}
                    {save ? (
                      <div className="cp-save">
                        <svg className="cp-tick" viewBox="0 0 20 20" aria-hidden="true">
                          <rect width="20" height="20" rx="4" />
                          <path d="M5.5 10.5l3 3 6-6.5" />
                        </svg>
                        <span>{cardKeptSentence(i.brand.name)}</span>
                      </div>
                    ) : null}
                    {autoBox ? (
                      <label className="cp-auto">
                        <input type="checkbox" checked={autoTick} onChange={(e) => setAutoTick(e.target.checked)} />
                        <span>
                          <b>Charge my card after each visit</b>
                          <small>No more invoices to pay. You get a receipt by email. Stop anytime.</small>
                        </span>
                      </label>
                    ) : null}
                  </div>
                  {err ? (
                    <div className="cp-err" role="alert">
                      {err}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="cp-pay"
                    disabled={busy || (tipAfter && tipCents <= 0)}
                    onClick={() => void confirm()}
                  >
                    {LOCK}
                    <span>
                      {busy
                        ? "Paying..."
                        : tipAfter
                          ? `Add a ${moneyExact(tipCents)} tip`
                          : kind === "klarna"
                            ? "Continue to Klarna"
                            : "Confirm and Pay"}
                    </span>
                  </button>
                  <p className="cp-legal">
                    By selecting {tipAfter ? "Add a tip" : kind === "klarna" ? "Continue to Klarna" : "Confirm and Pay"}, you
                    allow {i.brand.name} to {autoAfter ? "charge" : "make a one-time charge of"} {moneyExact(payCents)}{" "}
                    {kind === "bank"
                      ? "to your bank account"
                      : kind === "klarna"
                        ? "through Klarna"
                        : savedCard
                          ? `to your ${brandWord(savedCard.brand)} ending ${savedCard.last4}`
                          : "to your card"}{" "}
                    today
                    {autoAfter
                      ? ". We keep your card on file and charge it after each visit for your repeat service"
                      : save
                        ? ` and to keep your card on file for future services`
                        : ""}
                    . {kind === "card" ? <CardTermsLink /> : null}
                  </p>
                  {kind === "card" ? (
                    <div className="cp-brands" aria-label="Cards we take">
                      <span className="cp-cb visa">VISA</span>
                      <span className="cp-cb mc">
                        <i />
                        <i />
                      </span>
                      <span className="cp-cb amex">AMEX</span>
                      <span className="cp-cb dsc">DISCOVER</span>
                    </div>
                  ) : null}
                  <div className="cp-trust">
                    <div>
                      <svg
                        viewBox="0 0 24 24"
                        width="22"
                        height="22"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="4" y="11" width="16" height="10" rx="2" />
                        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                      </svg>
                      <span>Payment details are encrypted</span>
                    </div>
                    <div>
                      <svg
                        viewBox="0 0 24 24"
                        width="22"
                        height="22"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
                        <path d="M9 12l2 2 4-4" />
                      </svg>
                      <span>Secure payments with Stripe</span>
                    </div>
                    <div>
                      <svg
                        viewBox="0 0 24 24"
                        width="22"
                        height="22"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
                        <path d="M9 8h6M9 12h6" />
                      </svg>
                      <span>Proof of payment for your records</span>
                    </div>
                  </div>
                  {website ? (
                    <p className="cp-pp">
                      <a href={`${website}/privacy-policy/`} target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                      </a>
                    </p>
                  ) : null}
                </>
              ) : (
                <p className="cp-note">
                  Online payment is not open for this invoice. Call us{i.brand.phone ? ` at ${prettyPhone(i.brand.phone)}` : ""}{" "}
                  to pay.
                </p>
              )
            ) : null}
          </div>
        </div>
        {i.photos.length ? (
          <div className="cp-photos" aria-label="Photos from the visit">
            {byYard(i.photos).map((ph) => {
              const label = yardLabel(ph.room);
              return (
                <figure key={ph.id}>
                  <img src={`/v1/public/pay/${token}/photos/${ph.id}`} alt={label ?? "Photo from the visit"} />
                  {label ? <figcaption>{label}</figcaption> : null}
                </figure>
              );
            })}
          </div>
        ) : null}
        {i.payNote && i.status === "open" ? <p className="cp-note">{i.payNote}</p> : null}
        {i.footer ? <p className="cp-note">{i.footer}</p> : null}
      </div>
    </Branded>
  );
}
